import React, { useState } from 'react';
import type { Facture, AppData } from '../types';
import { Button, Card, Input, Select } from './UI';

interface FacturesListProps {
  data: AppData;
  onDataChange: (data: AppData) => void;
}

export function FacturesList({ data, onDataChange }: FacturesListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Facture>>({});

  const dossiers = data.dossiers;
  const factures = data.factures.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      id: `f${Date.now()}`,
      dossier: dossiers[0]?.id || '',
      numero: `F${new Date().getFullYear()}-${String(data.factures.length + 1).padStart(4, '0')}`,
      montant: 0,
      date: new Date().toISOString().split('T')[0],
      statut: 'En attente',
    });
  };

  const handleEdit = (facture: Facture) => {
    setEditingId(facture.id);
    setFormData(facture);
  };

  const handleSave = () => {
    if (!formData.dossier || !formData.numero || !formData.montant || !formData.date) {
      alert('Veuillez remplir les champs requis');
      return;
    }

    const newData = { ...data };
    const dossier = newData.dossiers.find((d) => d.id === formData.dossier);

    if (isCreating) {
      newData.factures.push(formData as Facture);
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Facture créée',
        detail: `${formData.numero} — ${formData.montant}€`,
        ts: new Date().toISOString(),
      });
    } else {
      newData.factures = newData.factures.map((f) =>
        f.id === editingId ? (formData as Facture) : f
      );
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Facture modifiée',
        detail: `${formData.numero}`,
        ts: new Date().toISOString(),
      });
    }

    onDataChange(newData);
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;

    const facture = data.factures.find((f) => f.id === id);
    const newData = { ...data };
    newData.factures = newData.factures.filter((f) => f.id !== id);
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: facture?.dossier || '',
      action: 'Facture supprimée',
      detail: facture?.numero || '',
      ts: new Date().toISOString(),
    });

    onDataChange(newData);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const updateStatus = (id: string, newStatus: string) => {
    const newData = { ...data };
    const facture = newData.factures.find((f) => f.id === id);
    if (facture) {
      facture.statut = newStatus as any;
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: facture.dossier,
        action: 'Statut facture mis à jour',
        detail: `${facture.numero} → ${newStatus}`,
        ts: new Date().toISOString(),
      });
      onDataChange(newData);
    }
  };

  if (isCreating || editingId) {
    const dossier = dossiers.find((d) => d.id === formData.dossier);

    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>
          {isCreating ? 'Créer une facture' : 'Éditer la facture'}
        </h1>

        <Card style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Dossier *
              </label>
              <Select
                value={formData.dossier || ''}
                onChange={(e) => setFormData({ ...formData, dossier: e.target.value })}
              >
                <option value="">Sélectionner un dossier</option>
                {dossiers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.ref} — {d.client}
                  </option>
                ))}
              </Select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  N° Facture *
                </label>
                <Input
                  value={formData.numero || ''}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  placeholder="Ex: F2024-0001"
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Montant (€) *
                </label>
                <Input
                  type="number"
                  value={formData.montant || ''}
                  onChange={(e) => setFormData({ ...formData, montant: parseFloat(e.target.value) || 0 })}
                  placeholder="Ex: 1500"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Statut *
                </label>
                <Select
                  value={formData.statut || 'En attente'}
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value as any })}
                >
                  <option value="En attente">⏳ En attente</option>
                  <option value="Acompte versé">💳 Acompte versé</option>
                  <option value="Partiellement réglé">⚠️ Partiellement réglé</option>
                  <option value="Soldé">✓ Soldé</option>
                  <option value="Impayé">❌ Impayé</option>
                </Select>
              </div>
            </div>

            {dossier && (
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: 'var(--border-radius-md)', fontSize: 12 }}>
                <strong>{dossier.ref}</strong> — {dossier.client}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
              <Button onClick={handleSave}>
                {isCreating ? 'Créer' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Stats
  const totalAmount = factures.reduce((sum, f) => sum + f.montant, 0);
  const paidAmount = factures
    .filter((f) => f.statut === 'Soldé' || f.statut === 'Acompte versé')
    .reduce((sum, f) => sum + f.montant, 0);
  const unpaidAmount = totalAmount - paidAmount;

  const statusColors = {
    'En attente': '#9e6a03',
    'Acompte versé': '#0969da',
    'Partiellement réglé': '#1f883d',
    'Soldé': '#1a7f37',
    'Impayé': '#cf222e',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Factures</h1>
        <Button onClick={handleCreate}>+ Créer une facture</Button>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Total facturé
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            {totalAmount}€
          </p>
        </Card>
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Payé
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1a7f37' }}>
            {paidAmount}€
          </p>
        </Card>
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Non payé
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#cf222e' }}>
            {unpaidAmount}€
          </p>
        </Card>
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Total factures
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            {factures.length}
          </p>
        </Card>
      </div>

      {factures.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {factures.map((facture) => {
            const dossier = dossiers.find((d) => d.id === facture.dossier);
            return (
              <Card key={facture.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 0.25rem', fontSize: 13, fontWeight: 600 }}>
                      {facture.numero}
                    </p>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {dossier?.ref} — {dossier?.client}
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                      {facture.date} • {facture.montant}€
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Select
                      value={facture.statut}
                      onChange={(e) => updateStatus(facture.id, e.target.value)}
                    >
                      <option value="En attente">⏳ En attente</option>
                      <option value="Acompte versé">💳 Acompte versé</option>
                      <option value="Partiellement réglé">⚠️ Partiellement réglé</option>
                      <option value="Soldé">✓ Soldé</option>
                      <option value="Impayé">❌ Impayé</option>
                    </Select>
                    <Button onClick={() => handleEdit(facture)} size="sm" variant="secondary">
                      ✎
                    </Button>
                    <Button onClick={() => handleDelete(facture.id)} size="sm" variant="secondary">
                      🗑
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Aucune facture enregistrée
          </p>
          <Button onClick={handleCreate}>Créer la première facture</Button>
        </Card>
      )}
    </div>
  );
}
